import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../app/hooks";
import TesSalaryTableReadOnlyGrid from "../../../tesSalaryTables/TesSalaryTableReadOnlyGrid";
import {
  fetchTesSalaryTablesByTes,
  TesSalaryTableDocument,
} from "../../../tesSalaryTables/tesSalaryTablesApi";
import {
  BULK_SALARY_INCREASE_FILTER_NAMES,
  BULK_SALARY_TES_TABLES_NON_TABLE_SOURCE_ID,
} from "../constants";
import { ToggleContainer } from "../styles";
import { BulkSalaryIncreaseFilterValues } from "../types";

/**
 * Collapsible read-only previews of the selected source and target published tables (TES_TABLES bulk flow).
 */
const BulkSalaryIncreaseTesTablesReferenceSection = () => {
  const { t } = useTranslation();
  const tesLabels = useAppSelector((state) => state.localized.tes);
  const { watch } = useFormContext<BulkSalaryIncreaseFilterValues>();
  const [open, setOpen] = useState(false);
  const [publishedTables, setPublishedTables] = useState<TesSalaryTableDocument[]>(
    [],
  );
  const [loadingTables, setLoadingTables] = useState(false);

  const tes = watch(BULK_SALARY_INCREASE_FILTER_NAMES.TES);
  const tesId = tes?.id ?? null;
  const sourceId = watch("tesSalaryTableSourceId");
  const targetId = watch("tesSalaryTableTargetId");

  useEffect(() => {
    if (!tesId) {
      setPublishedTables([]);
      return;
    }
    let cancelled = false;
    setLoadingTables(true);
    fetchTesSalaryTablesByTes(tesId, { status: "PUBLISHED" })
      .then((rows) => {
        if (!cancelled) {
          setPublishedTables(rows);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPublishedTables([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingTables(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tesId]);

  const sourceTable =
    publishedTables.find((doc) => doc.tesSalaryTableId === sourceId) ?? null;
  const targetTable =
    publishedTables.find((doc) => doc.tesSalaryTableId === targetId) ?? null;
  const tesLabel = tesId ? tesLabels[tesId] : undefined;

  const hasAnySelection = Boolean(sourceId || targetId);
  const canShowGrids = hasAnySelection && !loadingTables;

  return (
    <Grid id="bulk-salary-increase-tes-tables-reference" item xs={12} mb={2}>
      <ToggleContainer onClick={() => setOpen((v) => !v)}>
        <Typography variant="header" fontWeight={500} fontSize="20px">
          {t("settings:bulkSalaryIncrease.tesTablesReferenceTitle")}
        </Typography>
        <IconButton size="small">
          {open ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </ToggleContainer>
      <Collapse in={open}>
        <Box sx={{ pt: 1, pb: 1 }}>
          {!hasAnySelection && (
            <Typography variant="secondary" color="text.secondary">
              {t("settings:bulkSalaryIncrease.tesTablesReferenceHint")}
            </Typography>
          )}
          {hasAnySelection && loadingTables && (
            <Box display="flex" alignItems="center" gap={1} py={2}>
              <CircularProgress size={22} />
              <Typography variant="secondary">
                {t("settings:bulkSalaryIncrease.tesTablesReferenceLoading")}
              </Typography>
            </Box>
          )}
          {canShowGrids && (
            <>
              <TesSalaryTableReadOnlyGrid
                table={sourceTable}
                tesLabel={tesLabel}
                sectionLabel={t(
                  "settings:bulkSalaryIncrease.tesTablesReferenceSourceHeading",
                )}
              />
              {!sourceTable && sourceId && sourceId !== BULK_SALARY_TES_TABLES_NON_TABLE_SOURCE_ID && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                  {t(
                    "settings:bulkSalaryIncrease.tesTablesReferenceMissing",
                    {
                      id: sourceId,
                    },
                  )}
                </Typography>
              )}
              {sourceId && targetId && <Divider sx={{ my: 2 }} />}
              {targetId && (
                <>
                  <TesSalaryTableReadOnlyGrid
                    table={targetTable}
                    tesLabel={tesLabel}
                    sectionLabel={t(
                      "settings:bulkSalaryIncrease.tesTablesReferenceTargetHeading",
                    )}
                  />
                  {!targetTable && (
                    <Typography color="error" variant="body2">
                      {t(
                        "settings:bulkSalaryIncrease.tesTablesReferenceMissing",
                        {
                          id: targetId,
                        },
                      )}
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Collapse>
      <Divider />
    </Grid>
  );
};

export default BulkSalaryIncreaseTesTablesReferenceSection;
